import { TranslateService } from '@ngx-translate/core';

/**
 * Global reference to TranslateService
 * Must be initialized in AppComponent constructor
 */
let translateService: TranslateService;

/**
 * Initialize the i18n utility with TranslateService instance
 * Call this in AppComponent constructor
 */
export function initI18nUtil(service: TranslateService): void {
    translateService = service;
}

/**
 * Short helper function for instant translation
 * @param key - Translation key (use I18nKeys for type safety)
 * @param params - Optional interpolation parameters
 * @returns Translated string
 *
 * @example
 * ```typescript
 * import { t, I18nKeys } from 'core/utils/i18n.util';
 *
 * const message = t(I18nKeys.MASS_WITHDRAWAL_CONFIRMATION);
 * const warning = t(I18nKeys.MASS_WITHDRAWAL_WARNING, { count: 5 });
 * ```
 */
export function t(key: string, params?: any): string {
    if (!translateService) {
        console.warn('[i18n.util] TranslateService not initialized. Call initI18nUtil() in AppComponent.');
        return key;
    }
    return translateService.instant(key, params);
}

/**
 * Alias for t() function
 */
export const i18n = t;

/**
 * App language codes → BCP 47 locales (Material date adapter, Date formatting).
 */
export const LANG_TO_LOCALE: Record<string, string> = {
    ua: 'uk-UA',
    ru: 'ru-RU',
    gb: 'en-GB',
    kk: 'kk-KZ',
};

/**
 * Resolve BCP 47 locale for an app language code.
 * Falls back to the provided language code, then ua → uk-UA.
 */
export function getLocaleForLang(langCode: string | null | undefined): string {
    if (!langCode) {
        return LANG_TO_LOCALE.ua;
    }
    return LANG_TO_LOCALE[langCode] || langCode;
}

/**
 * Map language code to flag filename
 * Some languages use different codes for flags (e.g., kk language uses kz.png flag)
 *
 * @param langCode - ISO 639-1 language code (or null)
 * @returns Flag filename without extension
 *
 * @example
 * ```typescript
 * getFlagCode('kk') // returns 'kz' (Kazakh flag)
 * getFlagCode('ua') // returns 'ua'
 * getFlagCode(null) // returns 'ua' (default)
 * ```
 */
export function getFlagCode(langCode: string | null): string {
    if (!langCode) {
        return 'ua';  // Default to Ukrainian flag if no language set
    }
    const flagMap: Record<string, string> = {
        'kk': 'kz',  // Kazakh: ISO code is 'kk', but flag file is kz.png
    };
    return flagMap[langCode] || langCode;
}

/**
 * Get the complete flag image path for a language code
 * Handles async pipe unwrapping and flag code mapping
 *
 * @param langCode - ISO 639-1 language code (or null)
 * @returns Complete relative path to flag image
 *
 * @example
 * ```typescript
 * // In template:
 * <img [src]="getCurrentFlagPath(currentLang$ | async)" />
 *
 * // In TypeScript:
 * const path = getCurrentFlagPath('kk'); // '../../../assets/images/kz.png'
 * ```
 */
export function getCurrentFlagPath(langCode: string | null): string {
    const flagCode = getFlagCode(langCode);
    return `../../../assets/images/${flagCode}.png`;
}

/**
 * Type-safe i18n keys with Ukrainian literal values shown on hover in VSCode
 * Generated from ua.json translation file
 *
 * Hover over any constant to see the actual Ukrainian translation!
 */
export const I18nKeys = {
    // ==================== Common ====================
    /** "Експорт" */
    COMMON_EXPORT: 'common.export',
    /** "csv" */
    COMMON_CSV: 'common.csv',
    /** "xlsx" */
    COMMON_XLSX: 'common.xlsx',
    /** "Невідомо" */
    COMMON_UNKNOWN: 'common.unknown',
    /** "Проект" */
    COMMON_PROJECT: 'common.project',
    /** "Організація" */
    COMMON_ORGANIZATION: 'common.organization',
    /** "ПК" */
    COMMON_PACKAGE: 'common.package',
    /** "Немає даних" */
    COMMON_EMPTY_DATA: 'common.empty-data',

    // ==================== Actions ====================
    /** "ОЧИСТИТИ" */
    ACTIONS_CLEAN: 'actions.clean',
    /** "СКАСУВАТИ" */
    ACTIONS_CANCEL: 'actions.cancel',
    /** "ЗБЕРЕГТИ" */
    ACTIONS_SAVE: 'actions.save',
    /** "Завантажити" */
    ACTIONS_DOWNLOAD: 'actions.download',
    /** "ЗГЕНЕРУВАТИ ЗВІТ" */
    ACTIONS_GENERATE_REPORT: 'actions.generate-report',
    /** "ЗАМОВИТИ ЗВІТ ТА ЗАКРИТИ" */
    ACTIONS_REQUEST_REPORT: 'actions.request-report',
    /** "ЗАМОВИТИ ЗВІТ" */
    ACTIONS_ORDER_REPORT: 'actions.order-report',
    /** "ЗБЕРЕГТИ ТА ЗАКРИТИ" */
    ACTIONS_SAVE_AND_CLOSE: 'actions.saveAndClose',
    /** "Створити" */
    ACTIONS_CREATE: 'actions.create',
    /** "Масове створення" */
    ACTIONS_CREATE_MULTI: 'actions.create-multi',
    /** "Наповнення ПК" */
    ACTIONS_PACKAGE_FILLING: 'actions.packageFilling',
    /** "Додати послуги в пакет" */
    ACTIONS_ADD_SERVICE: 'actions.addService',
    /** "Видалити послуги з пакету" */
    ACTIONS_DELETE_SERVICE: 'actions.deleteService',
    /** "ОК" */
    ACTIONS_OK: 'ok',
    /** "ЗБЕРЕГТИ І НАДІСЛАТИ ПАРОЛЬ" */
    ACTIONS_SAVE_AND_SEND: 'actions.saveAndSend',
    /** "Надіслати пароль" */
    ACTIONS_SEND_PASSWORD: 'actions.sendPassword',
    /** "Пароль надісланний на пошту" */
    ACTIONS_SEND_TO_EMAIL: 'actions.sendToEmail',
    /** "Пароль надіслано" */
    ACTIONS_PASS_SEND: 'actions.passSend',
    /** "Закрити" */
    ACTIONS_CLOSE: 'actions.close',
    /** "Закрити?" */
    ACTIONS_CLOSE_CONFIRMATION: 'actions.close-confirmation',
    /** "Надіслати" */
    ACTIONS_SEND: 'actions.send',
    /** "Об'єднати клієнтів" */
    ACTIONS_CONNECT_CUSTOMERS: 'actions.connect-customers',
    /** "Об'єднати" */
    ACTIONS_MERGE: 'actions.merge',
    /** "ВИКОНАТИ ОБ'ЄДНАННЯ" */
    ACTIONS_PERFORM_MERGING: 'actions.perform-merging',
    /** "НОВИЙ КЛІЄНТ" */
    ACTIONS_NEW_CLIENT: 'actions.new-client',
    /** "ОТРИМАТИ" */
    ACTIONS_GET_REPORT: 'actions.get-report',

    // ==================== Mass Withdrawal ====================
    /** "Масове виведення з ПК" */
    MASS_WITHDRAWAL_TITLE: 'mass-withdrawal.title',
    /** "Фільтри для пошуку записів Участь в ПК" */
    MASS_WITHDRAWAL_FILTER_TITLE: 'mass-withdrawal.filter-title',
    /** "Встановити для знайдених записів наступні значення" */
    MASS_WITHDRAWAL_SET_TITLE: 'mass-withdrawal.set-title',
    /** "Дата включення в ПК з" */
    MASS_WITHDRAWAL_FROM_DATE_FROM: 'mass-withdrawal.from-date-from',
    /** "Дата включення в ПК по" */
    MASS_WITHDRAWAL_FROM_DATE_TO: 'mass-withdrawal.from-date-to',
    /** "Дата виходу з ПК" */
    MASS_WITHDRAWAL_DATE_TO: 'mass-withdrawal.date-to',
    /** "Причина виходу з ПК" */
    MASS_WITHDRAWAL_REASON_EXITING: 'mass-withdrawal.reason-exiting-package',
    /** "За даними параметрами клієнтів не знайдено" */
    MASS_WITHDRAWAL_CLIENTS_EMPTY: 'mass-withdrawal.clients-empty',
    /** "Увага! {{count}} клієнта(ів) буде виведено з ПК." */
    MASS_WITHDRAWAL_WARNING: 'mass-withdrawal.warning-message',
    /** "Ви впевнені?" */
    MASS_WITHDRAWAL_CONFIRMATION: 'mass-withdrawal.confirmation',
    /** "{{count}} клієнта(ів) виведено з ПК" */
    MASS_WITHDRAWAL_SUCCESS: 'mass-withdrawal.success-message',

    // ==================== Meetings ====================
    /** "Консультант" */
    MEETINGS_CONSULTANT: 'meetingsNew.consultant',
    /** "Клієнт" */
    MEETINGS_CLIENT: 'meetingsNew.client',
    /** "Дата" */
    MEETINGS_DATE: 'meetingsNew.date',
    /** "Початок" */
    MEETINGS_DATE_START: 'meetingsNew.dateStart',
    /** "Кінець" */
    MEETINGS_DATE_END: 'meetingsNew.dateEnd',
    /** "Стан" */
    MEETINGS_STATUS: 'meetingsNew.status',
    /** "Верифікувати" */
    MEETINGS_VERIFICATION: 'meetingsNew.verification',
    /** "Спочатку необхідно обрати дату." */
    MEETINGS_CHOICE_DATA: 'meetingsNew.choice-data',
    /** "Верифікувати зустріч?" */
    MEETINGS_POPUP_VERIFICATION: 'meetingsNew.popup.isverification',
    /** "Послуга" */
    MEETINGS_SERVICE: 'meetingsNew.service',
    /** "Зберегти в..." */
    MEETINGS_SAVE_AS: 'meetingsNew.saveAs',
    /** "Експорт" */
    MEETINGS_EXPORT: 'meetingsNew.export',
    /** "Послуги" */
    MEETINGS_SERVICES: 'meetingsNew.services',
    /** "csv" */
    MEETINGS_CSV: 'meetingsNew.csv',
    /** "xlsx" */
    MEETINGS_XLSX: 'meetingsNew.xlsx',
    /** "Послуги (розширений)" */
    MEETINGS_SERVICE_EXTENDS: 'meetingsNew.service-extends',
    /** "між" */
    MEETINGS_BETWEEN: 'meetingsNew.between',
    /** "Ви підтверджуєте надання послуг?" */
    MEETINGS_CONFIRM_PROVISION: 'meetingsNew.confirm-provision',
    /** "Невалідні зустрічі" */
    MEETINGS_INVALID: 'meetingsNew.invalid-meetings',
    /** "Створено" */
    MEETINGS_CREATED_STATUS: 'meetingsNew.created-status',
    /** "Заплановану кількість надання цієї послуги вичерпано для цього соціального працівника" */
    MEETINGS_LIMIT_APP_TASK_COUNT_FOR_CONSULTANT: 'createMeeting.limitAppTaskCountForConsultant',
    /** "Неможливо відновити послугу: заплановану кількість надання вичерпано для цього соціального працівника" */
    MEETINGS_NOT_POSSIBLE_TO_RESTORE_SERVICE: 'createMeeting.notPossibleToRestoreService',
    /** "Відновлено лише послуги, які не перевищують ліміт, встановлений у розподілі завдань для цього консультанта. При необхідності перевірте ліміти у розподілі, змініть їх і відновіть решту послуг окремо" */
    MEETINGS_RESTORED_ONLY_SERVICES_THAT_NOT_EXCEED_LIMIT: 'createMeeting.restoredOnlyServicesThatNotExceedLimit',
    /** "Заплановану кількість надання цієї послуги для цього соціального працівника вичерпано або її недостатньо для обраної кількості клієнтів" */
    MEETINGS_LIMIT_APP_TASK_COUNT_FOR_CONSULTANT_OR_NOT_ENOUGH_FOR_NUMBER_CLIENTS: 'createMeeting.limitAppTaskCountForConsultantOrNotEnoughtForNumberClients',

    // ==================== My Reports ====================
    /** "Звіт" */
    MYREPORTS_NAME: 'myreports.name',
    /** "Дата створення" */
    MYREPORTS_CREATE_DATE: 'myreports.createDate',
    /** "Тип репорту" */
    MYREPORTS_REPORT_TYPE: 'myreports.reportType',
    /** "Дата з" */
    MYREPORTS_DATE_FROM: 'myreports.dateFrom',
    /** "Дата по" */
    MYREPORTS_DATE_TO: 'myreports.dateTo',
    /** "Регіони" */
    MYREPORTS_REGIONS: 'myreports.regions',
    /** "Організації" */
    MYREPORTS_ORGANIZATIONS: 'myreports.organizations',
    /** "ПК" */
    MYREPORTS_PACKAGES: 'myreports.packages',
    /** "Послуги" */
    MYREPORTS_SERVICES: 'myreports.services',
    /** "Індикатори" */
    MYREPORTS_INDICATORS: 'myreports.indicators',
    /** "Детально?" */
    MYREPORTS_DETAILED: 'myreports.detailed',
    /** "Стан" */
    MYREPORTS_STATUS: 'myreports.status',
    /** "Тип партнера" */
    MYREPORTS_PARTNER_TYPE: 'myreports.partnerType',
    /** "Згода на тестування" */
    MYREPORTS_TESTING_AGREEMENT: 'myreports.testingAgreement',
    /** "Тип випадку" */
    MYREPORTS_CASE_RESULT: 'myreports.caseResult',
    /** "Клієнти" */
    MYREPORTS_CLIENTS: 'myreports.clients',
    /** "Пункти надання послуг" */
    MYREPORTS_SUBRECIPIENT_ORGS: 'myreports.subrecipientOrganizations',
    /** "Надавачі послуг" */
    MYREPORTS_APP_USERS: 'myreports.appUsers',
    /** "Всі" */
    MYREPORTS_ALL: 'myreports.all',
    /** "Нові" */
    MYREPORTS_NEW: 'myreports.new',
    /** "Позитивно" */
    MYREPORTS_POSITIVE: 'myreports.positive',
    /** "Негативно" */
    MYREPORTS_NEGATIVE: 'myreports.negative',
    /** "Інший звіт" */
    MYREPORTS_REPORT_TYPE_OTHER: 'myreports.reportTypes.other',
    /** "Невизначена" */
    MYREPORTS_STATUS_UNDEFINED: 'myreports.statuses.undefined',

    // ==================== Project Dialog ====================
    /** "Додати обрані в проект" */
    PROJECT_DIALOG_ADD_SELECTED: 'project-dialog.add-selected',
    /** "Видалити обрані з проекту" */
    PROJECT_DIALOG_DELETE_SELECTED: 'project-dialog.delete-selected',
    /** "Пакети" */
    PROJECT_DIALOG_PACKAGES: 'project-dialog.packages',
    /** "Пакети в проекті" */
    PROJECT_DIALOG_PACKAGES_IN_PROJECT: 'project-dialog.packages-in-project',
    /** "НАЗВА" */
    PROJECT_DIALOG_NAME_LABEL: 'project-dialog.name-label',
    /** "К-сть" */
    PROJECT_DIALOG_COUNT_LABEL: 'project-dialog.count-label',
    /** "Верифікація" */
    PROJECT_DIALOG_VERIFICATION_LABEL: 'project-dialog.verification-label',
    /** "ТРИВАЛІСТЬ" */
    PROJECT_DIALOG_DURATION_LABEL: 'project-dialog.duration-label',
    /** "хв" */
    PROJECT_DIALOG_DURATION_UNIT: 'project-dialog.duration-unit',
    /** "МАКС К-сть в ПОДІЇ" */
    PROJECT_DIALOG_MAX_COUNT_LABEL: 'project-dialog.max-count-label',
    /** "РЕЗУЛЬТАТ" */
    PROJECT_DIALOG_RESULT_LABEL: 'project-dialog.result-label',

    // ==================== Project Component ====================
    /** "Опис проекту" */
    PROJECT_DESCRIPTION: 'projectComponent.descriptionProject',
    /** "Дата з" */
    PROJECT_DATE_START: 'projectComponent.dateStart',
    /** "Дата по" */
    PROJECT_DATE_TO: 'projectComponent.dateTo',
    /** "Донор" */
    PROJECT_DONOR: 'projectComponent.donor',
    /** "Реципієнт" */
    PROJECT_RECIPIENT: 'projectComponent.recipient',
    /** "Контракт" */
    PROJECT_CONTRACT: 'projectComponent.contract',
    /** "Дата по повинна бути більше ніж дата з" */
    PROJECT_COMPARE_DATE: 'projectComponent.compareDate',
    /** "Наповнення проекту" */
    PROJECT_FILLING: 'projectComponent.filling',
    /** "розподілення завдань" */
    PROJECT_DISTRIBUTION: 'projectComponent.distribution',
    /** "В проекті є пакети. Видалення неможливе" */
    PROJECT_PACKAGE_IN_PROJECT: 'projectComponent.packageInProject',
    /** "Необхідність способу залучення" */
    PROJECT_INVOLVEMENT_METHOD: 'projectComponent.involvement-method',
    /** "Колір" */
    PROJECT_COLOR_PICKER: 'projectComponent.color-picker',
    /** "Налаштування Послуг" */
    PROJECT_SETTING_SERVICES: 'projectComponent.setting-services',

    // ==================== Contracts ====================
    /** "Новий договір є дублікатом" */
    CONTRACTS_NEW_CONTRACT_IS_DUPLICATE: 'contracts.new-contract-is-duplicate',

    // ==================== Periods ====================
    /** "Періоди" */
    PERIODS_PERIODS: 'periods.periods',
    /** "Період" */
    PERIODS_PERIOD: 'periods.period',
    /** "Проект" */
    PERIODS_PROJECT: 'periods.project',
    /** "Організація" */
    PERIODS_ORGANIZATION: 'periods.organization',
    /** "Назва" */
    PERIODS_NAME: 'periods.name',
    /** "Дата з" */
    PERIODS_DATE_FROM: 'periods.dateFrom',
    /** "Дата по" */
    PERIODS_DATE_TO: 'periods.dateTo',
    /** "Статус" */
    PERIODS_STATUS: 'periods.status',
    /** "Масове створення" */
    PERIODS_MULTIPLE_CREATE: 'periods.multiple-create',
    /** "Масове редагування" */
    PERIODS_MULTIPLE_EDIT: 'periods.multiple-edit',
    /** "Немає періодів за вказаними параметрами" */
    PERIODS_MULTIPLE_EMPTY: 'periods.multiple-periods-empty',
    /** "Змінити статус на" */
    PERIODS_CHANGE_STATUS_TO: 'periods.change-status-to',

    // ==================== Error Text ====================
    /** "Поле не може бути пустим" */
    ERROR_EMPTY: 'errorText.emptyError',
    /** "Необхідно обрати елемент списку" */
    ERROR_EMPTY_OBJECT: 'errorText.emptyObject',
    /** "Поле повинно містити тільки латинські літери" */
    ERROR_ONLY_LATIN: 'errorText.onlyLatin',
    /** "Поле повине бути додатним числом" */
    ERROR_ONLY_POSITIVE: 'errorText.onlyPositive',
    /** "Має бути додатне ціле число" */
    ERROR_ONLY_POSITIVE_INTEGER: 'errorText.onlyPositiveAndWithOutDots',
    /** "Невірний формат поля" */
    ERROR_WRONG_FORMAT: 'errorText.wrongFormat',
    /** "E-mail не валідний" */
    ERROR_WRONG_FORMAT_EMAIL: 'errorText.wrongFormatEmail',
    /** "Невірний формат дати" */
    ERROR_INVALID_DATE: 'errorText.invalidDate',
    /** "Недопустима дата" */
    ERROR_INVALID_DATE_FORMAT: 'errorText.invalidDateFormat',
    /** "Недопустиме значення" */
    ERROR_INVALID_VALUE: 'errorText.invalidValue',
    /** "Виберіть значення зі списку" */
    ERROR_SELECT_VALUE_FROM_LIST: 'errorText.selectValueFromTheList',
    /** "Ціле число" */
    ERROR_INTEGER: 'errorText.integer',
    /** "Оберіть організацію зі списку" */
    ERROR_SELECT_ORGANIZATION: 'errorText.select-organization',
    /** "Оберіть проєкт зі списку" */
    ERROR_SELECT_PROJECT: 'errorText.select-project',
    /** "Оберіть ПК зі списку" */
    ERROR_SELECT_PC: 'errorText.select-pc',
    /** "Невірний формат кількості" */
    ERROR_INVALID_COUNT_FORMAT: 'errorText.invalid-count-format',
    /** "Номер існує в системі." */
    ERROR_PHONE_EXISTS_IN_SYSTEM: 'errorText.phone-exists-in-system',

    // ==================== Form Common ====================
    /** "Редагування" */
    FORM_EDITING: 'form-common.editing',
    /** "Виберіть значення зі списку" */
    FORM_SELECT_FROM_LIST: 'form-common.select-from-list',

    // ==================== Date Range ====================
    /** "Дата з - Дата по" */
    DATE_RANGE_FROM_TO: 'date-range.from-to',
    /** "Дата з" */
    DATE_RANGE_DATE_FROM: 'date-range.date-from',
    /** "Дата по" */
    DATE_RANGE_DATE_TO: 'date-range.date-to',
    /** "Невірний формат \"Дата з\"" */
    DATE_RANGE_INVALID_DATE_FROM: 'date-range.invalid-date-from',
    /** "Невірний формат \"Дата по\"" */
    DATE_RANGE_INVALID_DATE_TO: 'date-range.invalid-date-to',
    /** "Введіть правильний формат дати" */
    DATE_RANGE_ENTER_CORRECT_FORMAT: 'date-range.enter-correct-format',
    /** "між" */
    DATE_RANGE_BETWEEN: 'date-range.between',

    // ==================== File Upload ====================
    /** "Завантажити файл" */
    FILE_UPLOAD_PLACEHOLDER: 'file-upload.placeholder',
    /** "Файл повинен бути менше 5 мегабайт" */
    FILE_UPLOAD_MAX_SIZE_ERROR: 'file-upload.max-size-error',
    /** "Файл повинен бути у форматі png" */
    FILE_UPLOAD_TYPE_ERROR_PNG: 'file-upload.type-error-png',
    /** "Замінити" */
    FILE_UPLOAD_REPLACE: 'file-upload.replace',
    /** "Обрати зображення" */
    FILE_UPLOAD_CHOOSE_IMAGE: 'file-upload.choose-image',

    // ==================== Locations ====================
    /** "Область" */
    LOCATIONS_REGION: 'locations.region',
    /** "Район" */
    LOCATIONS_DISTRICT: 'locations.district',
    /** "Населений пункт" */
    LOCATIONS_SETTLEMENT: 'locations.settlement',

    // ==================== Currency ====================
    /** " грн." */
    CURRENCY_UAH_SUFFIX: 'currency.uah-suffix',
    /** " тенге" */
    CURRENCY_KZT_SUFFIX: 'currency.kzt-suffix',

    // ==================== Auth ====================
    /** "Авторизація" */
    AUTH_AUTHORIZATION: 'auth.authorization',
    /** "Телефон або електронна адреса" */
    AUTH_EMAIL_OR_PHONE: 'auth.email-or-phone',
    /** "Пароль" */
    AUTH_PASSWORD: 'auth.password',
    /** "УВІЙТИ" */
    AUTH_ENTER: 'auth.enter',
    /** "Реєстрація" */
    AUTH_REGISTRATION: 'auth.registration',
    /** "UserName" */
    AUTH_USERNAME: 'auth.username',
    /** "E-mail" */
    AUTH_EMAIL: 'auth.email',
    /** "Прізвище" */
    AUTH_LAST_NAME: 'auth.last-name',
    /** "Ім'я" */
    AUTH_FIRST_NAME: 'auth.first-name',
    /** "По батькові" */
    AUTH_MIDDLE_NAME: 'auth.middle-name',
    /** "Телефон" */
    AUTH_PHONE: 'auth.phone',
    /** "Індивідуальний номер ІС МСЗХ" */
    USERS_AIDS_ORG_CODE: 'users.AIDSOrgCode',
    /** "УІК" */
    USERS_UIC: 'users.uic',
    /** "Роль" */
    AUTH_ROLE: 'auth.role',
    /** "Авторизуватися" */
    AUTH_LOGIN_LINK: 'auth.login-link',

    // ==================== User Roles ====================
    /** "Адміністратор" */
    USER_ROLE_ADMIN: 'userRole.admin',
    /** "Адміністратор донора" */
    USER_ROLE_ADMIN_DONOR: 'userRole.admin-donor',
    /** "Адміністратор реципієнта" */
    USER_ROLE_ADMIN_RECIPIENT: 'userRole.admin-recipient',
    /** "Адміністратор субреципієнта" */
    USER_ROLE_ADMIN_SUBRECIPIENT: 'userRole.admin-subrecipient',
    /** "Консультант" */
    USER_ROLE_CONSULTANT: 'userRole.consultant',
    /** "Клієнт" */
    USER_ROLE_CLIENT: 'userRole.client',

    // ==================== Organizations ====================
    /** "Не вибрано жодної організації у список \"Відноситься до організації\". Список не може бути пустим." */
    ORG_NO_PARENT_ORG_SELECTED: 'organizations.no-parent-org-selected',
    /** "Неможливо активувати звʼязок з організацією для пункту надання послуг. Спочатку активуйте картку пункту надання послуг" */
    ORG_CANNOT_ACTIVATE_RELATION: 'organizations.cannot-activate-relation',
    /** "Організація-Дублікат має більше однієї ролі. Неможливо перенести дані з організації, яка має додаткову роль" */
    ORG_MERGE_MULTIPLE_ROLES_ERROR: 'organizations.merge-multiple-roles-error',

    // ==================== Appeal ====================
    /** "Направити до адміністратора" */
    APPEAL_SEND_TO_ADMIN: 'appeal.send-to-admin',
    /** "год" */
    APPEAL_HOURS: 'appeal.hours',
    /** "Так" */
    APPEAL_YES: 'appeal.yes',
    /** "Ні" */
    APPEAL_NO: 'appeal.no',
    /** "Для переходу в стан "Завершено" необхідно додати коментар параюриста з описом проведеної роботи" */
    APPEAL_FILL_COMMENT_FIELD: 'appeal.fill-comment-field',
    /** "Сфера правопорушення" */
    APPEAL_VIOLATION_SPHERE: 'appeal.violation-sphere',
    /** "Група правопорушення" */
    APPEAL_VIOLATION_GROUP: 'appeal.violation-group',
    /** "Вид правопорушення" */
    APPEAL_VIOLATION_KIND: 'appeal.violation-kind',
    /** "В роботі у організації" */
    APPEAL_IN_WORK_AT_ORGANIZATION: 'appeal.in-work-at-organization',
    /** "Відповідальний" */
    APPEAL_RESPONSIBLE: 'appeal.responsible',
    /** "Юридична організація" */
    APPEAL_LEGAL_ORGANIZATION: 'appeal.legal-organization',
    /** "Відповідальний юрист" */
    APPEAL_RESPONSIBLE_LAWYER: 'appeal.responsible-lawyer',
    /** "Регіон" */
    APPEAL_REGION: 'appeal.region',
    /** "Район" */
    APPEAL_DISTRICT: 'appeal.district',
    /** "Населений пункт" */
    APPEAL_SETTLEMENT: 'appeal.settlement',
    /** "Місце правопорушення" */
    APPEAL_VIOLATION_LOCATION: 'appeal.violation-location',
    /** "Дата народження" */
    APPEAL_DATE_OF_BIRTH: 'appeal.date-of-birth',
    /** "Дата порушення" */
    APPEAL_DATE_OF_VIOLATION: 'appeal.date-of-violation',
    /** "Дата звернення" */
    APPEAL_DATE_OF_REQUEST: 'appeal.date-of-request',
    /** "Дата завершення" */
    APPEAL_DATE_OF_COMPLETION: 'appeal.date-of-completion',
    /** "Дата порушення з" */
    APPEAL_DATE_OF_VIOLATION_FROM: 'appeal.date-of-violation-from',
    /** "Дата порушення по" */
    APPEAL_DATE_OF_VIOLATION_TO: 'appeal.date-of-violation-to',
    /** "Дата звернення з" */
    APPEAL_DATE_OF_REQUEST_FROM: 'appeal.date-of-request-from',
    /** "Дата звернення по" */
    APPEAL_DATE_OF_REQUEST_TO: 'appeal.date-of-request-to',
    /** "Дата завершення з" */
    APPEAL_DATE_OF_COMPLETION_FROM: 'appeal.date-of-completion-from',
    /** "Дата завершення по" */
    APPEAL_DATE_OF_COMPLETION_TO: 'appeal.date-of-completion-to',
    /** Для переходу у стан "Взято в роботу", "Перенаправлено до юриста" потрібно змінити значення "Не вказано" */
    APPEAL_CHANGE_VALUE_NOT_SPECIFIED: 'appeal.change-value-not-specified',

    // ==================== Date Labels ====================
    /** "Дата" */
    DATE_LABEL: 'date.label',
    /** "Дата з" */
    DATE_FROM_LABEL: 'date.from-label',
    /** "Дата по" */
    DATE_TO_LABEL: 'date.to-label',
    /** "між" */
    DATES_RANGE_BETWEEN: 'datesRange.between',

    // ==================== Settlement Types ====================
    /** "с." */
    SETTLEMENT_TYPE_VILLAGE: 'settlement.type.village',
    /** "м." */
    SETTLEMENT_TYPE_CITY: 'settlement.type.city',
    /** "с-ще" */
    SETTLEMENT_TYPE_URBAN_VILLAGE: 'settlement.type.urban-village',
    /** "смт." */
    SETTLEMENT_TYPE_URBAN_SETTLEMENT: 'settlement.type.urban-settlement',

    // ==================== Organization Names ====================
    /** "Пункт надання послуг" */
    ORG_SERVICE_DELIVERY_POINT: 'organizations.service-delivery-point',
    /** "Організація" */
    ORG_ORGANIZATION: 'organizations.organization',
    /** "На дому у клієнтів" */
    ORG_NAME_AT_CLIENTS_HOME: 'organizations.name.at-clients-home',
    /** "На базі організації" */
    ORG_NAME_ORGANIZATION_BASED: 'organizations.name.organization-based',
    /** "На вулиці" */
    ORG_NAME_ON_STREET: 'organizations.name.on-street',
    /** "Аутріч" */
    ORG_NAME_OUTREACH: 'organizations.name.outreach',
    /** "Місцезнаходження клієнта" */
    ORG_NAME_CLIENT_LOCATION: 'organizations.name.client-location',
    /** "Відділення" */
    ORG_NAME_BRANCH: 'organizations.name.branch',
    /** "Кабінет довіри" */
    ORG_NAME_TRUST_OFFICE: 'organizations.name.trust-office',

    // ==================== Common Answers ====================
    /** "Так" */
    YES: 'yes',
    /** "Ні" */
    NO: 'no',
    /** "ОК" */
    OK: 'ok',

    // ==================== Auto-Verification (DCM-1490, DCM-1491) ====================
    /** "Спроба автоматичного підтвердження особи без прив'язаного користувача" */
    AUTO_VERIFICATION_WITHOUT_USER_ATTEMPTED: 'autoVerification.withoutUserAttempted',
    /** "Спроба автоматичного підтвердження особи з прив'язаним користувачем" */
    AUTO_VERIFICATION_WITH_USER_ATTEMPTED: 'autoVerification.withUserAttempted',
    /** "Особу автоматично підтверджено" */
    AUTO_VERIFICATION_SUCCESSFUL: 'autoVerification.successful',
    /** "Не вдалося автоматично підтвердити особу" */
    AUTO_VERIFICATION_FAILED: 'autoVerification.failed',
    /** "Підтвердження особи" */
    AUTO_VERIFICATION_NOTIFICATION_TITLE: 'autoVerification.notificationTitle',
    /** "Вашу особу автоматично підтверджено в системі DC Care" */
    AUTO_VERIFICATION_NOTIFICATION_BODY: 'autoVerification.notificationBody',

    // ==================== Client Register ====================
    /** "Неможливо створити лікувальне призначення, якщо клієнт не має статусу "Позитивний"" */
    CANT_CREATE_PROPHILAXIS_WITHOUT_POSITIVE_STATUS: 'clientRegister.cantCreateProphilaxisWithoutPositiveStatusMessage'
} as const;

/**
 * Type for all i18n key values
 */
export type I18nKey = typeof I18nKeys[keyof typeof I18nKeys];
